import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {
    EntityData,
    EntityDataStatus,
    EntityModelStatus,
    provideEntityModel,
    provideSearchModel
} from "@fedeltapointofsale/data-client";
import {ProductLineEntityModel} from "../product-line-entity.model";
import {AbstractEditPage} from "../../../framework/entity/edit/edit-page/abstract-edit-page";
import {Plugin} from "../../../config";
import {TaxSearchModel} from "../../../system/tax/tax-search.model";
import {ExtraSearchPageComponent} from "../../extras/extra-search-page/extra-search-page.component";
import {
    ProductContainerSearchPageComponent
} from "../../product-container/product-container-search-page/product-container-search-page.component";
import {
    ProductContainerEditPageComponent
} from "../../product-container/product-container-edit-page/product-container-edit-page.component";
import {MembershipTypeSearchModel} from "../../../customers/membership-type/membership-type-search.model";
import {MembershipTypeData} from "../../../customers/membership-type/membership-type.data";
import {
    MembershipTypeEditPageComponent
} from "../../../customers/membership-type/membership-type-edit-page/membership-type-edit-page.component";
import {debounceTime, first, Subscription} from "rxjs";
import {ProductLineData, ProductLineItemData} from "../product-line.data";
import {CostedInventoryQuantity, StockCosting} from "../../../stock/costing/stock-costing.data";
import {StockCostingType} from "../../../stock/costing/stock-costing-type";
import {InventoryEditPageComponent} from "../../../stock/inventory/inventory-edit-page/inventory-edit-page.component";
import {StockCostingService} from "../../../stock/costing/stock-costing.service";
import {
    InventorySearchPageComponent
} from "../../../stock/inventory/inventory-search-page/inventory-search-page.component";
import {EditGridReorderEvent} from "../../../framework/entity/edit/edit-grid/edit-grid-reorder-event";
import {MetaTagDefinitionData, MetaTagDefinitionReferenceData, MetaTagType} from "../../meta-tag/meta-tag.data";
import {
    PreparationModifierSearchPageComponent
} from "../../preparation-modifier/preparation-modifier-search-page/preparation-modifier-search-page.component";
import {
    PreparationModifierEditPageComponent
} from "../../preparation-modifier/preparation-modifier-edit-page/preparation-modifier-edit-page.component";
import {EditPageComponent} from "../../../framework/entity/edit/edit-page/edit-page.component";
import {
    SellableEditPriceLevelsComponent
} from "../../sellable/sellable-edit-price-levels/sellable-edit-price-levels.component";
import {
    EntityFormCollectionControl
} from "../../../framework/entity/edit/entity-form-builder/entity-form-collection-control";
import {
    ProductLineRecipeCardReportModel
} from "../../reports/productlinerecipecard/product-line-recipe-card-report.model";
import {
    ProductLineRecipeIngredientData
} from "../../reports/productlinerecipecard/product-line-recipe-card-report.data";
import {FUIAlertService, FUIResponsiveService} from "@fedeltapointofsale/ui";
import {
    PreparationResourceTypeEditPageComponent
} from "../../../order-processing/preparation-resource-type/preparation-resource-type-edit-page/preparation-resource-type-edit-page.component";
import {
    PreparationResourceTypeSearchPageComponent
} from "../../../order-processing/preparation-resource-type/preparation-resource-type-search-page/preparation-resource-type-search-page.component";
import {EditMode} from "../../../framework/entity/edit/edit-page/edit-mode";
import {
    DocketSelectorSearchPageComponent
} from "../../../order-processing/docket-selector/docket-selector-search-page/docket-selector-search-page.component";
import {
    DocketSelectorEditPageComponent
} from "../../../order-processing/docket-selector/docket-selector-edit-page/docket-selector-edit-page.component";
import {MemberTypeLoyaltySpecData} from "../../sellable/sellable.data";
import {ExtraReferenceData} from "../../extras/extra.data";

@Component({
    selector: "wbo-product-line-edit-page",
    templateUrl: "./product-line-edit-page.component.html",
    styleUrls: ["./product-line-edit-page.component.scss"],
    providers: [
        provideEntityModel(ProductLineEntityModel),
        provideSearchModel(TaxSearchModel),
        provideSearchModel(MembershipTypeSearchModel),
        ProductLineRecipeCardReportModel,
        StockCostingService
    ]
})
export class ProductLineEditPageComponent extends AbstractEditPage implements OnInit, OnDestroy {
    Plugin = Plugin;
    EditMode = EditMode;
    ExtraSearchPageComponent = ExtraSearchPageComponent;
    ProductContainerSearchPageComponent = ProductContainerSearchPageComponent;
    ProductContainerEditPageComponent = ProductContainerEditPageComponent;
    MembershipTypeEditPageComponent = MembershipTypeEditPageComponent;
    InventorySearchPageComponent = InventorySearchPageComponent;
    InventoryEditPageComponent = InventoryEditPageComponent;
    MetaTagType = MetaTagType;
    DocketSelectorSearchPageComponent = DocketSelectorSearchPageComponent;
    DocketSelectorEditPageComponent = DocketSelectorEditPageComponent;
    PreparationModifierSearchPageComponent = PreparationModifierSearchPageComponent;
    PreparationModifierEditPageComponent = PreparationModifierEditPageComponent;
    PreparationResourceTypeEditPageComponent = PreparationResourceTypeEditPageComponent;
    PreparationResourceTypeSearchPageComponent = PreparationResourceTypeSearchPageComponent;

    @ViewChild(EditPageComponent) editPage: EditPageComponent;
    @ViewChild(SellableEditPriceLevelsComponent) sellableEditPriceLevels: SellableEditPriceLevelsComponent;
    loyaltySpecData: MemberTypeLoyaltySpecData[];
    inventoryCostingType: StockCostingType;
    inventoryCostingOutletId: number;
    totalCost: number;
    docketSelectors: MetaTagDefinitionReferenceData[] = [];
    preparationModifiers: MetaTagDefinitionReferenceData[] = [];
    private subscriptions: Subscription = new Subscription();
    private inventoryChangeSubscription: Subscription;
    private taxChangeSubscription: Subscription;
    private tagsLoadedSubscription: Subscription;

    constructor(public model: ProductLineEntityModel,
                public taxSearchModel: TaxSearchModel,
                private membershipTypeSearchModel: MembershipTypeSearchModel,
                private stockCostingService: StockCostingService,
                private productLineRecipeCardReportModel: ProductLineRecipeCardReportModel,
                private alertService: FUIAlertService,
                changeDetectorRef: ChangeDetectorRef,
                responsiveService: FUIResponsiveService) {
        super(changeDetectorRef, responsiveService);
        this.model.$data.pipe(first()).subscribe(() => {
            if (this.model.status === EntityModelStatus.INSERT) {
                this.taxSearchModel.search().pipe(first()).subscribe(() => {
                    const taxes = [];
                    this.taxSearchModel.data.filter((tax) => tax.defaultTax).forEach((tax) => {
                        taxes.push(tax);
                    });
                    this.model.data.taxes = taxes;
                });
            }
        });
    }

    ngOnInit(): void {
        this.subscriptions.add(this.membershipTypeSearchModel.search().subscribe(() => {
            this.subscriptions.add(this.model.$data.subscribe(() => {
                this.loyaltySpecData = [];
                this.addMembershipToList(undefined);
                this.membershipTypeSearchModel.data.forEach((membershipType) => {
                    this.addMembershipToList(membershipType);
                });
            }));
        }, () => {
            // @todo should be dealt with
        }));
        this.inventoryChangeSubscription = this.model.$collectionChanged("productInventory.items").pipe(debounceTime(500)).subscribe(() => {
            this.inventoryChanged();
        });
        this.taxChangeSubscription = this.model.$collectionChanged("taxes").pipe(debounceTime(500)).subscribe(() => {
            this.taxChanged();
        });
        this.subscriptions.add(this.model.$data.subscribe(() => {
            if (this.tagsLoadedSubscription) {
                this.tagsLoadedSubscription.unsubscribe();
            }
            this.tagsLoadedSubscription = this.model.$collectionLoaded("tags").subscribe(() => {
                this.loadMetaTags();
            });
            // load tags
            const forceLoad = this.model.data.tags;
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        if (this.inventoryChangeSubscription) {
            this.inventoryChangeSubscription.unsubscribe();
        }
        if (this.taxChangeSubscription) {
            this.taxChangeSubscription.unsubscribe();
        }
        if (this.tagsLoadedSubscription) {
            this.tagsLoadedSubscription.unsubscribe();
        }
    }

    addMembershipToList(membershipType: MembershipTypeData): void {
        let memberTypeLoyaltySpecData: MemberTypeLoyaltySpecData = this.model.data.memberLoyalty.memberTypeSpecs.find((value) => value.membershipType.membershipTypeId ===
            (membershipType ? membershipType.membershipTypeId : undefined));

        if (!memberTypeLoyaltySpecData) {
            memberTypeLoyaltySpecData = <MemberTypeLoyaltySpecData>EntityData.create(MemberTypeLoyaltySpecData);
            if (membershipType) {
                memberTypeLoyaltySpecData.membershipType.assign(membershipType, EntityDataStatus.LOADED);
            }
        }

        this.loyaltySpecData.push(memberTypeLoyaltySpecData);
    }

    memberLoyaltySaved(data: MemberTypeLoyaltySpecData): void {
        const index = this.model.data.memberLoyalty.memberTypeSpecs.findIndex((value) => value.membershipType.membershipTypeId === data.membershipType.membershipTypeId);
        if (index === -1) {
            this.model.data.memberLoyalty.memberTypeSpecs.push(data);
        }
    }

    inventoryChanged(): void {
        this.costStock();
        this.sellableEditPriceLevels.calculatePricing();
    }

    taxChanged(): void {
        this.sellableEditPriceLevels.calculatePricing();
    }

    costStock(): void {
        if (this.model.data.productInventory.items.length === 0 || this.model.data.productInventory.items.find((productLineItem: ProductLineItemData) => productLineItem[EntityFormCollectionControl.FLAG_CHILD_ERROR])) {
            this.totalCost = null;
            return;
        }
        const stockCosting: StockCosting = <StockCosting>EntityData.create(StockCosting);
        stockCosting.costingType = this.inventoryCostingType;
        stockCosting.outletId = this.inventoryCostingOutletId;
        stockCosting.basePrice = this.model.data.price;
        stockCosting.baseCostEx = this.model.data.baseCostEx;
        stockCosting.inventoryQuantities = this.model.data.productInventory.items.map((productLineItem: ProductLineItemData) => CostedInventoryQuantity.fromProductLineItem(productLineItem));

        this.model.data.productInventory.items.forEach((productLineItem: ProductLineItemData) => {
            productLineItem.cost = null;
        });
        this.totalCost = null;

        this.stockCostingService.performAction(stockCosting)
            .then((result: any) => {
                stockCosting.assign(result);
                this.totalCost = stockCosting.totalCost;
                stockCosting.inventoryQuantities.forEach((costedInventoryQuantity: CostedInventoryQuantity) => {
                    const productLineItem: ProductLineItemData = this.model.data.productInventory.items.find((item: ProductLineItemData) => item.inventory.inventoryId === costedInventoryQuantity.inventoryId);
                    if (productLineItem) {
                        productLineItem.cost = costedInventoryQuantity.cost;
                    }
                });
            });
    }

    selectMetaTag(metaTags: any[]): void {
        metaTags.forEach((metaTag) => {
            if (!this.model.data.tags.find((tag) => tag.metaTagDefinitionId === metaTag.metaTagDefinitionId)) {
                const newEntity: MetaTagDefinitionData = <MetaTagDefinitionData>EntityData.create(MetaTagDefinitionData);
                newEntity.assign(metaTag);
                newEntity.localisableContext.universal = true;
                this.model.data.tags.push(newEntity);
                this.editPage.editService.dirty = true;
                this.loadMetaTags();
            }
        });
    }

    metaTagRemoved(metaTag: MetaTagDefinitionData): void {
        this.model.data.tags.splice(this.model.data.tags.findIndex(value => value.metaTagDefinitionId === metaTag.metaTagDefinitionId), 1);
        this.editPage.editService.dirty = true;
        this.loadMetaTags();
    }

    preparationModifierReordering(event: EditGridReorderEvent): void {
        const fromId: number = this.preparationModifiers[event.fromIndex].metaTagDefinitionId;
        const toId: number = this.preparationModifiers[event.toIndex].metaTagDefinitionId;
        const fromIndex: number = this.model.data.tags.findIndex((item) => item.metaTagDefinitionId === fromId);
        const toIndex: number = this.model.data.tags.findIndex((item) => item.metaTagDefinitionId === toId);
        const record: any = this.model.data.tags[fromIndex];
        this.model.data.tags[fromIndex] = this.model.data.tags[toIndex];
        this.model.data.tags[toIndex] = record;
    }

    addingAnother(previousEntity: ProductLineData): void {
        this.model.data.primaryCategory.categoryId = previousEntity.primaryCategory.categoryId;
        this.model.data.taxes = previousEntity.taxes;
    }

    private loadMetaTags(): void {
        this.docketSelectors = this.model.data.tags.filter(value => value.type === this.MetaTagType.DOCKET_SELECTOR);
        this.preparationModifiers = this.model.data.tags.filter(value => value.type === this.MetaTagType.PREPARATION);
    }

    downloadRecipeCard(): void {
        if (!this.model.data.productInventory.items.length) {
            this.alertService.showInformation("wbo.product-line.edit.prompt.cannotPrintRecipeCardNoItems.heading",
                "wbo.product-line.edit.prompt.cannotPrintRecipeCardNoItems.message").subscribe(() => {
            });
            return;
        }
        this.productLineRecipeCardReportModel.data.recipeMethod = this.model.data.productLineRecipeCard.recipeText;
        this.productLineRecipeCardReportModel.data.recipeImage = this.model.data.productLineRecipeCard.recipeImage;
        this.productLineRecipeCardReportModel.data.name = this.model.data.name;
        this.productLineRecipeCardReportModel.data.ingredients = this.model.data.productInventory.items.map(inventory => {
            const entity: ProductLineRecipeIngredientData = <ProductLineRecipeIngredientData>EntityData.create(ProductLineRecipeIngredientData);
            entity.name = inventory.inventory.name;
            entity.amount = inventory.amount;
            entity.stockSize = inventory.stockSize;
            return entity;
        });
        this.reportGenerationService.execute(this.productLineRecipeCardReportModel);
    }

    afterExtraAdded(extra: ExtraReferenceData): void {
        extra.localisableChildContext.universal = true;
    }
}
